import { Column, SortingFn, Table } from "@tanstack/react-table";
import { toInstantOrNow } from "utils/time-utils";
import { Row } from "./DatasetDataInputTableTypes";

export const sortByDate: SortingFn<Row> = (a, b, id) => {
    const timeA = toInstantOrNow(a.getValue(id)).toEpochMilli();
    const timeB = toInstantOrNow(b.getValue(id)).toEpochMilli();
    return timeA - timeB;
};

export const handleCopy = (
    e: React.KeyboardEvent<HTMLDivElement>,
    selectedCells: Set<string>,
    rows: Row[],
    table: Table<Row>
): void => {
    e.preventDefault();

    const allCols = table.getAllColumns();
    const coords = Array.from(selectedCells)
        .map(key => key.split("-").map(Number))
        .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

    const minRow = Math.min(...coords.map(([r]) => r));
    const maxRow = Math.max(...coords.map(([r]) => r));
    const minCol = Math.min(...coords.map(([_, c]) => c));
    const maxCol = Math.max(...coords.map(([_, c]) => c));

    const text = Array.from({ length: maxRow - minRow + 1 })
        .map((_, rOffset) => {
            const rowIdx = minRow + rOffset;
            return Array.from({ length: maxCol - minCol + 1 })
                .map((__, cOffset) => {
                    const colIdx = minCol + cOffset;
                    const col = allCols[colIdx];
                    const val = rows[rowIdx]?.[col.id];
                    return val ?? "";
                })
                .join("\t");
        })
        .join("\n");

    navigator.clipboard.writeText(text);
};

export const handleDelete = (
    e: React.KeyboardEvent<HTMLDivElement>,
    selectedCells: Set<string>,
    rows: Row[],
    table: Table<Row>,
    editingCell: { row: number; col: number } | null,
    registerUpdate: (colId: string, timestamp: string, value: string | null) => void,
    setRows: (newRows: Row[]) => void,
    pushHistory: (newRows: Row[]) => void
): void => {
    if (editingCell || !["Delete", "Backspace"].includes(e.key)) return;

    e.preventDefault();

    const newRows = [...rows];
    const allCols = table.getAllColumns();

    for (const key of selectedCells) {
        const [r, c] = key.split("-").map(Number);
        const col = allCols[c];
        if (!col?.columnDef.meta?.editable) continue;

        const colId = col.id;
        const timestamp = newRows[r]?.id;
        if (!timestamp) continue;

        newRows[r][colId] = null;
        registerUpdate(colId, timestamp, null);
    }
    pushHistory(newRows);
    setRows(newRows);
};

const findNextEditableCol = (startCol: number, direction: 1 | -1, allCols: Column<Row>[]): number => {
    let next = startCol + direction;
    while (next >= 0 && next < allCols.length) {
        if (allCols[next]?.columnDef?.meta?.editable) return next;
        next += direction;
    }
    return startCol;
};

export const handleSelectionNavigation = (
    e: React.KeyboardEvent<HTMLDivElement>,
    editingCell: { row: number; col: number } | null,
    selectedCells: Set<string>,
    table: Table<Row>,
    setSelectedCells: (cells: Set<string>) => void
): void => {
    if (editingCell) return;
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

    const selected = Array.from(selectedCells);
    if (selected.length !== 1) return;

    e.preventDefault();

    const [r, c] = selected[0].split("-").map(Number);
    const allCols = table.getAllColumns();

    let nr = r;
    let nc = c;

    if (e.key === "ArrowDown") nr++;
    if (e.key === "ArrowUp") nr--;
    if (e.key === "ArrowRight") nc = findNextEditableCol(c, 1, allCols);
    if (e.key === "ArrowLeft") nc = findNextEditableCol(c, -1, allCols);

    nr = Math.max(0, nr);
    nc = Math.max(0, Math.min(allCols.length - 1, nc));

    const newSel = new Set<string>([`${nr}-${nc}`]);
    setSelectedCells(newSel);
};


export const handleEditNavigation = (
    e: React.KeyboardEvent<HTMLDivElement>,
    table: Table<Row>,
    rows: Row[],
    editingCell: { row: number; col: number } | null,
    editValue: string,
    registerUpdate: (colId: string, timestamp: string, value: string | null) => void,
    setEditingCell: (cell: { row: number; col: number } | null) => void,
    setEditValue: (value: string) => void,
    setSelectedCells: (cells: Set<string>) => void,
    pushHistory: (newRows: Row[]) => void
): void => {
    if (
        !editingCell ||
        !["Enter", "Tab", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)
    )
        return;

    e.preventDefault();

    const allCols = table.getAllColumns();
    const colId = allCols[editingCell.col].id;
    const updated = [...rows];
    const timestamp = updated[editingCell.row].id;

    updated[editingCell.row][colId] = editValue === "" ? null : editValue;
    pushHistory(updated);

    registerUpdate(colId, timestamp, editValue === "" ? null : editValue);

    let nr = editingCell.row;
    let nc = editingCell.col;

    if (["Enter", "ArrowDown"].includes(e.key)) nr++;
    if (e.key === "ArrowUp") nr--;
    if (["ArrowRight", "Tab"].includes(e.key)) nc = findNextEditableCol(editingCell.col, 1, allCols);
    if (e.key === "ArrowLeft") nc = findNextEditableCol(editingCell.col, -1, allCols);

    nr = Math.max(0, Math.min(rows.length - 1, nr));
    nc = Math.max(0, Math.min(allCols.length - 1, nc));

    const newSel = new Set<string>([`${nr}-${nc}`]);
    setSelectedCells(newSel);

    const isEditableNext = allCols[nc]?.columnDef?.meta?.editable;
    if (isEditableNext) {
        const nextValue = rows[nr]?.[allCols[nc].id] ?? "";
        setEditingCell({ row: nr, col: nc });
        setEditValue(nextValue?.toString() ?? "");
    } else {
        setEditingCell(null);
        setEditValue("");
    }
};
